/**
 * SIMPLIFIED PDF Translation Approach
 * 
 * Create a simple translate function that works like the original,
 * but accesses the JSON directly without React context.
 */

import globalTranslations from '../../../translations/global.json';
import chomDakarOverlay from '../../../translations/tenant/chom-dakar.json';
import { resolveTenantTranslations, TenantOverlayRegistry } from '../../../translations/tenantOverlay';

type LanguageCode = 'en' | 'es' | 'fr';

// Simple language index mapping
const LANG_INDEX = { en: 0, es: 1, fr: 2 };

// SHER-371 — PDFs render outside the React/redux tree, so they never receive the
// per-tenant label overrides LocalizeProvider applies at app init (SHER-211).
// Re-apply the same overlay here, resolved from VITE_APP_API_URL, so PDF labels
// match the in-app UI — e.g. CHOM Dakar's `health_id` → "Dossier CHOM".
// This registry mirrors providerSherwood.jsx's TENANT_OVERLAYS; keep the two in
// sync when a new tenant overlay is added.
const TENANT_OVERLAYS: TenantOverlayRegistry = { 'chom-dakar': chomDakarOverlay };
const translations = resolveTenantTranslations(
  globalTranslations,
  import.meta.env.VITE_APP_API_URL,
  TENANT_OVERLAYS,
);

/**
 * Simple translate function that mimics react-localize-redux
 * Usage: t('pages.hospital.request.pdf.completion_date')
 * Returns the translation key if translation is not found (to spot missing translations)
 */
export const createPDFTranslate = (language: LanguageCode = 'fr') => {
  return (key: string): string => {
    try {
      const keys = key.split('.');
      let translation: any = translations;
      
      // Navigate to the translation
      for (const k of keys) {
        translation = translation?.[k];
        if (!translation) {
          // Translation path not found, return key to spot missing translations
          return key;
        }
      }
      
      // Get the translation for the language
      if (Array.isArray(translation)) {
        const langIndex = LANG_INDEX[language];
        const result = translation[langIndex];
        return result || key;
      }
      
      return translation || key;
    } catch (error) {
      return key;
    }
  };
};

// Export a default instance for French
export const t = createPDFTranslate('fr');

// Create instances for other languages
export const tEn = createPDFTranslate('en');
export const tEs = createPDFTranslate('es');

// Or create a dynamic version with locale normalization
export const createT = (locale: string) => {
  // Normalize locale: ensure it's 'en', 'es', or 'fr'
  const normalized = locale?.toLowerCase().startsWith('en') ? 'en' :
                     locale?.toLowerCase().startsWith('es') ? 'es' : 'fr';
  return createPDFTranslate(normalized as LanguageCode);
};

const getLeaf = (tree: any, key: string): any =>
  key.split('.').reduce((node, k) => (node == null ? undefined : node[k]), tree);

/**
 * SHER-371 — true when the active tenant's literals overlay redefines `key`,
 * i.e. the resolved leaf differs from the base `global.json` leaf. `resolveTenantTranslations`
 * returns the base tree by reference when no overlay applies, and `applyTenantOverlay`
 * replaces an overridden leaf with a *new* array — so a referential `!==` is exactly
 * "this tenant has its own value for this key".
 *
 * Lets PDF content be scoped to tenants that actually name a field, with the tenant
 * literals file (`translations/tenant/<slug>.json`) as the single source of truth — no
 * hard-coded tenant slug. Returns false for the base / no-overlay case, so other tenants
 * and localhost are unaffected.
 *
 * Only meaningful for LEAF keys (the `[en, es, fr]` translation arrays). The
 * `Array.isArray` guard fences off object-node keys: `applyTenantOverlay` rebuilds every
 * ancestor object on an override path, so an object node would compare non-identical and
 * give a spurious `true` even if the specific leaf you care about never changed.
 */
export const hasTenantOverride = (key: string): boolean => {
  const base = getLeaf(globalTranslations, key);
  return Array.isArray(base) && getLeaf(translations, key) !== base;
};
